import React from "react";
import { Helmet } from "react-helmet-async";
import type { SignalPost } from "../../types/signal";

const SITE_ORIGIN = "https://obsidianridge.ai";
const SITE_NAME = "Obsidian Ridge AI";
// Designed 1200x630 share card. Keep in sync with DEFAULT_OG_IMAGE in
// worker/seo.ts so SSR and hydrated meta agree.
const FALLBACK_IMAGE = `${SITE_ORIGIN}/og-default.png`;

/**
 * Build the JSON-LD schema for a single Signal post. Article posts emit
 * `Article` (eligible for Top Stories / news rich-results); Video posts
 * emit `VideoObject` (eligible for video carousels and rich snippets).
 * Gallery posts currently share the `Article` output path. This
 * function also appends `BreadcrumbList` and `WebPage`/
 * `SpeakableSpecification` blobs for all post kinds.
 *
 * The function is exported so the worker (and any tests) can build the
 * exact same blob server-side. Keep this in sync with worker/seo.ts.
 */
export const buildSignalPostJsonLd = (post: SignalPost): Record<string, unknown>[] => {
  const canonical = `${SITE_ORIGIN}/signal/${post.slug}`;
  const blobs: Record<string, unknown>[] = [];

  const publisher = {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_ORIGIN,
    logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/favicon.png` },
  };

  if (post.kind === "Video" && post.youtubeId) {
    // VideoObject - Google video rich-result eligible.
    const thumb = post.image || `https://i.ytimg.com/vi/${post.youtubeId}/hqdefault.jpg`;
    const embedUrl = `https://www.youtube.com/embed/${post.youtubeId}`;
    const contentUrl = `https://www.youtube.com/watch?v=${post.youtubeId}`;
    const videoBlob: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: post.title,
      description: post.excerpt,
      thumbnailUrl: [thumb],
      uploadDate: post.date,
      embedUrl,
      contentUrl,
      publisher,
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      keywords: post.tags.join(", "),
    };
    if (post.durationSeconds && post.durationSeconds > 0) {
      // ISO 8601 duration (PT#H#M#S)
      const h = Math.floor(post.durationSeconds / 3600);
      const m = Math.floor((post.durationSeconds % 3600) / 60);
      const s = post.durationSeconds % 60;
      let iso = "PT";
      if (h > 0) iso += `${h}H`;
      if (m > 0) iso += `${m}M`;
      if (s > 0 || iso === "PT") iso += `${s}S`;
      videoBlob.duration = iso;
    }
    blobs.push(videoBlob);
  } else {
    // Article (used for Article + Gallery kinds).
    blobs.push({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: post.image || FALLBACK_IMAGE,
      datePublished: post.date,
      // Mirror worker SSR (worker/seo.ts) so client hydration doesn't
      // flip dateModified for posts with an updated_at timestamp.
      dateModified: post.updatedAt ?? post.date,
      author: { "@type": "Person", name: post.author },
      publisher,
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      articleSection: post.tags[0] || "AI Engineering Signal",
      keywords: post.tags.join(", "),
      isBasedOn: post.sourceUrl,
    });
  }

  // BreadcrumbList - Home → Signal Desk → Post
  blobs.push({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: "Signal Desk", item: `${SITE_ORIGIN}/signal` },
      { "@type": "ListItem", position: 3, name: post.title, item: canonical },
    ],
  });

  // Speakable - let voice assistants read the headline + lede.
  blobs.push({
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: canonical,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "h1 + p", "main p:first-of-type", "article p:first-of-type"],
    },
  });

  return blobs;
};

interface SignalPostMetaProps {
  post: SignalPost;
}

/**
 * Helmet-only component that emits the full SEO / OG / Twitter / JSON-LD
 * block for a Signal post. Used by:
 *   1. The standalone `/signal/:slug` page (one-shot mount).
 *   2. The modal overlay route in `App.tsx` so that when a post is open
 *      over a background route (e.g. `/`), the post's metadata still
 *      wins client-side dedupe in `react-helmet-async`.
 *
 * The worker emits an equivalent block server-side (worker/seo.ts) so
 * crawlers always see post-specific tags in raw HTML before the SPA
 * hydrates.
 */
export const SignalPostMeta: React.FC<SignalPostMetaProps> = ({ post }) => {
  const canonical = `${SITE_ORIGIN}/signal/${post.slug}`;
  const ogType = post.kind === "Video" ? "video.other" : "article";
  // Mirror worker/seo.ts fallback chain so client-rendered meta matches
  // SSR tags after hydration: prefer the editor-set image, then the
  // YouTube thumbnail for Video posts, then the default 1200x630 card.
  const ogImage =
    post.image ||
    (post.kind === "Video" && post.youtubeId
      ? `https://i.ytimg.com/vi/${post.youtubeId}/hqdefault.jpg`
      : FALLBACK_IMAGE);
  const description = post.excerpt;
  // Title: `{headline} | Obsidian Ridge AI` when the headline is 40 chars or
  // fewer; otherwise the bare headline (a 41-60 char headline plus the
  // 20-char suffix would blow Google's ~60-char display budget, so the
  // suffix is dropped rather than truncated).
  // Keep in sync with buildSignalPostTitle in worker/seo.ts.
  const fullTitle = post.title.length <= 40 ? `${post.title} | ${SITE_NAME}` : post.title;
  // `updated_at` may be null for legacy rows; fall back to publish date so
  // crawlers always see a valid `article:modified_time`.
  const modifiedTime = post.updatedAt ?? post.date;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={post.title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      {/* Dimensions are only known for our own default card; post images vary.
          Mirrors worker/seo.ts buildHeadBlock. */}
      {ogImage === FALLBACK_IMAGE && <meta property="og:image:width" content="1200" />}
      {ogImage === FALLBACK_IMAGE && <meta property="og:image:height" content="630" />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="article:published_time" content={post.date} />
      <meta property="article:modified_time" content={modifiedTime} />
      <meta property="article:author" content={post.author} />
      <meta property="article:section" content={post.tags[0] || "AI Engineering Signal"} />
      {post.tags.map((tag) => (
        <meta property="article:tag" content={tag} key={tag} />
      ))}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@ObsidianRidgeAI" />
      <meta name="twitter:title" content={post.title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {buildSignalPostJsonLd(post).map((blob, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(blob).replace(/</g, "\\u003c")}
        </script>
      ))}
    </Helmet>
  );
};
